import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { imgUrl, WHATSAPP_URL } from "@/api";

export const Lightbox = ({ items, index, onClose, onNavigate }) => {
  const item = index != null ? items[index] : null;

  const prev = useCallback(
    () => onNavigate((index - 1 + items.length) % items.length),
    [index, items.length, onNavigate]
  );
  const next = useCallback(
    () => onNavigate((index + 1) % items.length),
    [index, items.length, onNavigate]
  );

  useEffect(() => {
    if (index == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, onClose, prev, next]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-xl flex flex-col"
          onClick={onClose}
          data-testid="lightbox-overlay"
        >
          <button
            onClick={onClose}
            aria-label="Kapat"
            data-testid="lightbox-close"
            className="absolute top-5 right-5 z-10 text-stone-300 hover:text-white transition-colors duration-300 p-2"
          >
            <X size={26} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Önceki"
            data-testid="lightbox-prev"
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 z-10 text-stone-300 hover:text-white transition-colors duration-300 p-3"
          >
            <ChevronLeft size={34} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Sonraki"
            data-testid="lightbox-next"
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 z-10 text-stone-300 hover:text-white transition-colors duration-300 p-3"
          >
            <ChevronRight size={34} />
          </button>

          <div className="flex-1 flex items-center justify-center px-6 md:px-24 pt-16 pb-4 min-h-0" onClick={(e) => e.stopPropagation()}>
            <motion.img
              key={item.id}
              src={imgUrl(item.image_url)}
              alt={item.title}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-full max-w-full object-contain shadow-2xl"
              data-testid="lightbox-image"
            />
          </div>

          <motion.div
            key={`info-${item.id}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="px-6 md:px-24 pb-8 md:pb-10"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-info"
          >
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold tracking-[0.25em] uppercase text-stone-400 mb-2" data-testid="lightbox-category">
                {item.category}
              </p>
              <h3 className="font-serif font-light text-3xl md:text-4xl text-stone-50 tracking-tight" data-testid="lightbox-title">
                {item.title}
              </h3>
              {item.description && (
                <p className="text-stone-400 font-light text-sm md:text-base mt-3 leading-relaxed" data-testid="lightbox-description">
                  {item.description}
                </p>
              )}
              <div className="mt-5 flex flex-wrap items-center gap-5">
                <span className="text-stone-300 text-sm font-light italic font-serif text-lg" data-testid="lightbox-price-note">
                  Fiyat için iletişime geçin
                </span>
                <a
                  href={`${WHATSAPP_URL}?text=${encodeURIComponent(`Merhaba, "${item.title}" hakkında bilgi almak istiyorum.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lightbox-whatsapp-button"
                  className="inline-flex items-center gap-2 border border-stone-500 text-stone-200 px-5 py-2.5 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-stone-50 hover:text-stone-900 transition-colors duration-300"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
